import React from 'react';
import { Skeleton, Button } from '@deriv-com/quill-ui';
import { localize } from '@deriv-com/translations';
import MobileMenuComponent from './mobile_menu';
import RiskDisclaimer from './risk-disclaimer';
import DFundedLoginModal from './dfunded_login_modal';
import { isDFundedLoginButtonVisible } from './dfunded_login_button';
import DFundedCore from '../../_common/base/dfunded/core';
import LanguageMenuModal from '../../../templates/_common/components/language-menu-modal';
import { renderReactComponent } from '../../_common/react_root_manager';
import { getElementById } from '../../_common/common_functions';
import Url from '../../_common/url';
import Login from '../../_common/base/login';
import Language from '../../_common/language';
import { AppProvider, useApp } from '../contexts/AppContext';
import {
    getBrandHomeUrl,
    getBrandUrl,
    getPlatformHostname,
} from '../../../templates/_common/brand.config';
import { getAccountType } from '../../config';
import { getDomainConfigSync } from '../../config/dynamic-config';

const BUILD_HASH = process.env.BUILD_HASH || '';

/**
 * HeaderLeft - Left section of header with logo and reports link
 */
const HeaderLeft = ({ onAccountClick }) => {
    const { isLoggedIn, toggleMobileMenu } = useApp();

    // Get dynamic config for site logo
    const siteConfig = getDomainConfigSync();
    const siteLogo = siteConfig?.logoPath || Url.urlForStatic(`images/pages/header/logo_smart_trader.svg?${BUILD_HASH}`);
    const siteName = siteConfig?.siteName || 'SmartTrader';

    return (
        <div className='header__menu-left'>
            <span className='header__hamburger--container'>
                <img
                    id='header__hamburger'
                    className='header__hamburger mobile-show'
                    src={Url.urlForStatic(
                        `images/pages/header/ic-hamburger.svg?${BUILD_HASH}`
                    )}
                    alt='Menu'
                    onClick={toggleMobileMenu}
                    style={{ cursor: 'pointer' }}
                />
            </span>
            {/* Site Logo - Links to trading page - visible on desktop */}
            <div className='header__menu-item header__menu-links mobile-hide'>
                <a
                    className='header__menu-links-item header__site-logo'
                    href='/trading'
                    title={siteName}
                >
                    <img
                        className='header__logo-img'
                        src={siteLogo}
                        alt={siteName}
                        style={{ height: '32px', maxWidth: '150px', objectFit: 'contain' }}
                    />
                </a>
            </div>

        </div>
    );
};

import AccountSwitcher from './account_switcher';

/**
 * AccountInfo - Displays account icon, type, and balance
 */
const AccountInfo = ({ onClick }) => {
    const { accountInfo, getFormattedBalance, getAccountTypeDisplay, conversion } = useApp();

    // Show loading skeleton for entire account info until we have data from authorize
    const isLoading =
    typeof accountInfo.balance === 'undefined' || !accountInfo.loginid;

    const isDemo = getAccountTypeDisplay().toLowerCase().includes('demo');
    const isConversionAccount = conversion.isOnConversionAccount();

    return (
        <div className='acc-info__wrapper' onClick={onClick} style={{ cursor: 'pointer' }}>
            <div className='account-info-wrapper'>
                {isLoading ? (
                    <div
                        data-testid='dt_acc_info mobile-hide'
                        id='dt_core_account-info_acc-info'
                        className='acc-info'
                    >
                        <div className='acc-info__content'>
                            <div className='acc-info__account-type-header'>
                                <Skeleton.Square width={60} height={16} />
                            </div>
                            <div className='acc-info__balance-section'>
                                <Skeleton.Square width={100} height={20} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        data-testid='dt_acc_info'
                        id='dt_core_account-info_acc-info'
                        className={`acc-info ${isConversionAccount ? 'acc-info--conversion' : ''}`}
                    >
                        <span className='acc-info__id' />
                        <div className='acc-info__content'>
                            <div className='acc-info__account-type-header'>
                                <p
                                    id='header__acc-type'
                                    className={`acc-info__account-type${
                                        isDemo ? ' acc-info__account-type--virtual' : ''
                                    }`}
                                >
                                    {getAccountTypeDisplay()}
                                </p>
                            </div>
                            <div className='acc-info__balance-section'>
                                <p
                                    data-testid='dt_balance'
                                    id='header__acc-balance'
                                    className='acc-info__balance'
                                >
                                    {getFormattedBalance()}
                                    <span className='symbols'>
                                        {' '}
                                        {accountInfo.currency || 'USD'}
                                    </span>
                                </p>
                                <img 
                                    className='acc-info__chevron' 
                                    src={Url.urlForStatic(`images/pages/header/ic-chevron-down.svg?${BUILD_HASH}`)} 
                                    alt=''
                                    style={{ marginLeft: '8px', width: '12px' }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * The DFunded login button follows two site switches (features.dfunded and
 * dfundedLoginButton, see dfunded_login_button.js). Re-read whenever the site
 * config (re)loads, so the button follows them without a reload.
 */
const useDFundedLoginButton = () => {
    const [isVisible, setIsVisible] = React.useState(isDFundedLoginButtonVisible);
    React.useEffect(() => {
        const update = () => setIsVisible(isDFundedLoginButtonVisible());
        update();
        window.addEventListener('smarttrader:configloaded', update);
        return () => window.removeEventListener('smarttrader:configloaded', update);
    }, []);
    return isVisible;
};

/**
 * LoginButtons - Login and signup buttons with skeleton loaders
 */
const LoginButtons = () => {
    const { isLoading } = useApp();
    const [isDFundedOpen, setIsDFundedOpen] = React.useState(false);
    const isDFundedLoginShown = useDFundedLoginButton();

    const handleLogin = (e) => {
        e.preventDefault();
        Login.redirectToLogin();
    };

    const handleSignup = (e) => {
        e.preventDefault();
        Login.redirectToSignup();
    };

    // Show skeleton loaders during token exchange
    if (isLoading || window.tokenExchangeInProgress) {
        return (
            <div className='header__btn'>
                <div className='skeleton-loaders-container'>
                    <Skeleton.Square
                        width={72}
                        height={32}
                        className='btn header__btn-login skeleton-btn-login'
                    />
                    <Skeleton.Square
                        width={72}
                        height={32}
                        className='btn header__btn-login skeleton-btn-signup'
                    />
                </div>
            </div>
        );
    }

    return (
        <div className='header__btn'>
            {/* Hidden when DFunded or its login button is switched off for this site. */}
            {isDFundedLoginShown && (
                <>
                    <a
                        id='btn__dfunded'
                        className='btn btn--tertiary header__btn-login'
                        title={localize('Log in with your DFunded account')}
                        onClick={(e) => { e.preventDefault(); setIsDFundedOpen(true); }}
                        href='#'
                    >
                        DFunded
                    </a>
                    <DFundedLoginModal isOpen={isDFundedOpen} onClose={() => setIsDFundedOpen(false)} />
                </>
            )}
            <a
                id='btn__login'
                className='btn btn--tertiary header__btn-login'
                onClick={handleLogin}
                href='#'
            >
                {localize('Log in')}
            </a>
            <a
                id='btn__signup'
                className='btn btn--primary header__btn-signup'
                onClick={handleSignup}
                href='#'
            >
                {localize('Sign up')}
            </a>
        </div>
    );
};

/**
 * HeaderRight - Right section with account info or login buttons
 */
const HeaderRight = () => {
    const { isLoggedIn, accountInfo } = useApp();
    const [isSwitcherOpen, setIsSwitcherOpen] = React.useState(false);

    if (isLoggedIn) {
        const hasCurrency = accountInfo.currency && accountInfo.currency !== '';
        const client = window.SmartTrader?.Client;
        const is_dfunded = !!(client?.isDFundedSession && client.isDFundedSession());

        const handleLogout = () => {
            // Use window.SmartTrader to access Client and avoid circular dependency
            if (window.SmartTrader?.Client?.sendLogoutRequest) {
                window.SmartTrader.Client.sendLogoutRequest(true);
            }
        };

        return (
            <div className='header__menu-right client_logged_in'>
                {/* Account info with balance - visible on both desktop and mobile */}
                <AccountInfo onClick={() => !is_dfunded && setIsSwitcherOpen(!isSwitcherOpen)} />
                {is_dfunded && (
                    <span
                        className='mobile-hide'
                        title={localize('Trading on your DFunded account')}
                        style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap', background: 'rgba(0,167,114,0.15)', color: '#00a772' }}
                    >
                        {/* The account-type header already reads "DFunded · Phase 1"; this names the program. */}
                        {DFundedCore.getDFundedProgramName() || 'DFunded'}
                    </span>
                )}
                {/* Account switcher dropdown/dialog */}
                {!is_dfunded && (
                    <AccountSwitcher
                        isVisible={isSwitcherOpen}
                        onClose={() => setIsSwitcherOpen(false)}
                    />
                )}
                {/* Logout button - desktop only, mobile has logout in sidebar */}
                <div className='mobile-hide'>
                    {!hasCurrency ? (
                        <Skeleton.Square
                            width={100}
                            height={32}
                            className='btn header__btn-logout'
                        />
                    ) : (
                        <Button
                            id='btn__logout'
                            className='btn header__btn-logout'
                            variant='primary'
                            size='md'
                            label={localize('Logout')}
                            onClick={handleLogout}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className='header__menu-right is-logout'>
            <LoginButtons />
        </div>
    );
};

/**
 * HeaderComponent - Main header component
 */
const HeaderComponent = () => (
    <div className='header' id='regular__header'>
        <div id='deriv__header' className='header__menu-items'>
            <HeaderLeft />
            <HeaderRight />
        </div>
        <MobileMenuComponent />
    </div>
);

export const init = () => {
    const container = getElementById('header-container');
    if (container) {
        renderReactComponent(
            <AppProvider>
                <>
                    <HeaderComponent />
                    <LanguageMenuModal />
                    <RiskDisclaimer />
                </>
            </AppProvider>,
            container
        );
    }
};

export default init;
